import { IOptionItem, ScalarType } from '@wyz/types';
import { IBaseTyreOffer } from '~/purchase/types';
import { Accordion, Button, Col, Icon, Row, Text, Title } from '@wyz/ui';
import { isTruthy } from '@wyz/utils';
import { get, isNil, reject, sumBy, uniqBy } from 'lodash-es';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { CheckboxField, CheckboxGroupField } from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import { IUserCustomer, TranslationKeys } from '~/common/types';
import { getFilterTranslation } from '~/purchase/helpers';
import { IBaseFilter, IProductListState, FilterFields } from '~/purchase/types';
import { useManageParameters } from '~/common/api';

// eslint-disable-next-line @wyz/module/no-relative-import
import './filter-bar.scss';
import { usePurchaseContext } from '~/purchase/context';
type FilterItemOption<V extends ScalarType> = IOptionItem<V> & {
  count: number;
  labelNode?: React.ReactNode;
};

const mapToOption = <T extends IBaseTyreOffer, V extends ScalarType>(
  rawArray: Array<T>,
  uniqField: string,
  getLabel: (item: T['tyre'], count: number) => string,
  getValue: (item: T['tyre']) => V,
  getCount: (item: T['tyre']) => number,
  getLabelNode?: (item: T['tyre'], count: number) => React.ReactNode,
): Array<FilterItemOption<V>> => {
  const isEmptyItem = (item: T) => !get(item, uniqField);
  const uniqArray = reject(uniqBy(rawArray, uniqField), isEmptyItem).filter(
    isTruthy,
  );
  return uniqArray.map((item) => {
    const count = getCount(item.tyre);
    return {
      label: getLabel(item.tyre, count),
      labelNode: getLabelNode?.(item.tyre, count),
      value: getValue(item.tyre),
      count,
    };
  });
};

const countedLabel = (i18nKey: keyof TranslationKeys, count: number) => (
  <>
    <Trans i18nKey={i18nKey} /> ({count})
  </>
);

type OtherFilter = {
  cond: boolean;
  name: string;
  label: keyof TranslationKeys;
};

type Props = {
  products: Array<IBaseTyreOffer>;
  listState: IProductListState;
  customer?: IUserCustomer;
  getFilterCount: <T extends FilterFields = FilterFields>(
    products: IBaseTyreOffer[],
    listState: IProductListState,
    field: T,
    values: IBaseFilter[T],
  ) => number;
};

const FilterAccordion = ({
  label,
  id,
  children,
}: React.PropsWithChildren<{
  label: React.ReactNode;
  id?: string;
}>) => {
  return (
    <Accordion.Container className='paddingless' flush>
      <Accordion.Item eventKey='0' id={id}>
        <Accordion.Header>
          <Text heading='xs' color='secondary-gray' weight='bold'>
            {label}
          </Text>
        </Accordion.Header>
        <Accordion.Body>{children}</Accordion.Body>
      </Accordion.Item>
    </Accordion.Container>
  );
};

function PurchaseFilterBar({ products, listState, getFilterCount }: Props) {
  const { t } = useI18n();
  const methods = useFormContext<IBaseFilter>();
  const manageParameters = useManageParameters({
    getSeasons: { enabled: true },
  });

  const allLabel = (count: number) => ({
    enabled: true,
    label: `${t('app.common.all_female')} (${count})`,
    labelNode: countedLabel('app.common.all_female', count),
  });

  const vehicleTypeOptions = mapToOption(
    products,
    'tyre.vehicleType.publicKey',
    (tyre, count) =>
      `${t(getFilterTranslation(tyre.vehicleType.name))} (${count})`,
    (tyre) => tyre.vehicleType.publicKey,
    (tyre) =>
      getFilterCount(products, listState, 'vehicleTypes', [
        tyre.vehicleType.publicKey,
      ]),
    (tyre, count) =>
      countedLabel(getFilterTranslation(tyre.vehicleType.name), count),
  ).filter(
    (option) =>
      option.count !== 0 ||
      listState.filters.vehicleTypes.includes(option.value),
  );

  const segmentOptions = mapToOption(
    products,
    'tyre.segment.publicKey',
    (tyre, count) => `${t(getFilterTranslation(tyre.segment.name))} (${count})`,
    (tyre) => tyre.segment.publicKey,
    (tyre) =>
      getFilterCount(products, listState, 'segments', [tyre.segment.publicKey]),
    (tyre, count) =>
      countedLabel(getFilterTranslation(tyre.segment.name), count),
  ).filter(
    (option) =>
      option.count !== 0 || listState.filters.segments.includes(option.value),
  );

  const brandOptions = mapToOption(
    products,
    'tyre.brand.publicKey',
    (tyre, count) => `${tyre.brand.name} (${count})`,
    (tyre) => tyre.brand.publicKey,
    (tyre) =>
      getFilterCount(products, listState, 'brands', [tyre.brand.publicKey]),
  ).filter(
    (option) =>
      option.count !== 0 || listState.filters.brands.includes(option.value),
  );
  const noHomologationCount = getFilterCount(
    products,
    listState,
    'homologations',
    [''],
  );
  const uniqueHomologationBrands = Array.from(
    new Set(
      products.flatMap((product) =>
        (product.tyre.homologations ?? []).map(
          (homologation) => homologation.brand,
        ),
      ),
    ),
  );
  const homologationOptions = [
    {
      label: `${t(
        'app.purchase.search_page.filter.no_homologation',
      )} (${noHomologationCount})`,
      labelNode: countedLabel(
        'app.purchase.search_page.filter.no_homologation',
        noHomologationCount,
      ),
      value: '',
      count: noHomologationCount,
    },
    ...uniqueHomologationBrands.map((homologation) => {
      const count = getFilterCount(products, listState, 'homologations', [
        homologation,
      ]);
      return {
        label: `${homologation} (${count})`,
        value: homologation,
        count,
      };
    }),
  ].filter(
    (option) =>
      option.count !== 0 ||
      listState.filters.homologations.includes(option.value),
  );

  const specificityOptions: Array<FilterItemOption<string>> = (
    [
      'electric',
      'sport',
      'eco',
      'reinforced',
      'sealRunFlat',
      'campingCar',
      'promotion',
      'studded',
      'studdable',
      'pmsf',
    ] as const
  )
    .map((spec) => {
      const count = getFilterCount(products, listState, 'specificities', [
        spec,
      ]);
      return {
        label: `${t(getFilterTranslation(spec))} (${count})`,
        labelNode: countedLabel(getFilterTranslation(spec), count),
        value: spec,
        count,
      };
    })
    .flat()
    .filter(
      (option) =>
        option.count !== 0 ||
        listState.filters.specificities.includes(option.value),
    );

  const seasonOptions = (manageParameters.getSeasons.data?.data ?? [])
    .map((season) => {
      const count = getFilterCount(products, listState, 'seasons', [season.id]);

      return {
        label: `${t(season.i18nKey)} (${count})`,
        labelNode: countedLabel(season.i18nKey, count),
        value: season.id,
        count,
      };
    })
    .filter(
      (option) =>
        option.count !== 0 || listState.filters.seasons.includes(option.value),
    );

  const availabilityCount = {
    total: getFilterCount(products, listState, 'availability', ['total']),
    partial: getFilterCount(products, listState, 'availability', ['partial']),
    supplierStockUnavailable: getFilterCount(
      products,
      listState,
      'availability',
      ['supplierStockUnavailable'],
    ),
    unavailable: getFilterCount(products, listState, 'availability', [
      'unavailable',
    ]),
    error: getFilterCount(products, listState, 'availability', ['error']),
  };
  const availabilityOptions: Array<FilterItemOption<string>> = (
    [
      ['total', 'app.purchase.search_page.filter.availabilities.total'],
      ['partial', 'app.purchase.search_page.filter.availabilities.partial'],
      [
        'supplierStockUnavailable',
        'app.purchase.search_page.filter.availabilities.supplier_stock_unavailable',
      ],
      [
        'unavailable',
        'app.purchase.search_page.filter.availabilities.unavailable',
      ],
      ['error', 'app.purchase.search_page.filter.availabilities.error'],
    ] as const satisfies ReadonlyArray<
      [keyof typeof availabilityCount, keyof TranslationKeys]
    >
  ).map(([value, i18nKey]) => {
    const count = availabilityCount[value];
    return {
      label: `${t(i18nKey)} (${count})`,
      labelNode: countedLabel(i18nKey, count),
      value,
      count,
    };
  });

  const inStockCount = products.filter(
    (product) =>
      (!isNil(product.customerStock) && product.customerStock > 0) ||
      product.proximityStocks.length !== 0,
  ).length;

  const purchaseCtx = usePurchaseContext();
  const j0 =
    purchaseCtx.state.targetCustomer?.settingTemplate?.alternativeOfferSetting
      .j0 ?? false;
  const otherFilters: OtherFilter[] = [
    {
      cond: j0,
      name: 'j0',
      label: 'app.purchase.search_page.filter.j0',
    },
  ];
  const enabledFilters = otherFilters.filter((filter) => filter.cond);

  const destockingCount = getFilterCount(products, listState, 'destocking', [
    'destocking',
  ]);
  const nonDestockingCount = getFilterCount(products, listState, 'destocking', [
    'nonDestocking',
  ]);
  const destockingOptions: Array<
    FilterItemOption<IBaseFilter['destocking'][number]>
  > = [
    {
      label: `${t('app.purchase.search_page.filter.destocking')} (${destockingCount})`,
      value: 'destocking',
      count: destockingCount,
    },
    {
      label: `${t('app.purchase.search_page.filter.non_destocking')} (${nonDestockingCount})`,
      value: 'nonDestocking',
      count: nonDestockingCount,
    },
  ];

  return (
    <>
      <Row className='flex-fill'>
        <div className='actions ps-2'>
          <Title heading='lg' weight='semi-bold'>
            <Trans i18nKey='app.purchase.search_page.filter.filters' />
          </Title>

          <Button
            id='reinitialize-filter'
            variant='link'
            size='xs'
            onClick={() => {
              methods.reset();
            }}
          >
            <Text
              heading='xs'
              color='secondary-gray'
              weight='semi-bold'
              className='d-flex align-items-center text-left text-nowrap'
            >
              <Icon icon='close-circle' size='xs' />
              <span>
                <Trans i18nKey='app.common.button.filter_reset' />
              </span>
            </Text>
          </Button>
          <hr className='filter-separator' />
        </div>
        <Col xl={12} xs={2}>
          <FilterAccordion
            label={<Trans i18nKey='app.common.product.vehicule_type' />}
          >
            <CheckboxGroupField
              name='vehicleTypes'
              options={vehicleTypeOptions}
              all={allLabel(sumBy(vehicleTypeOptions, 'count'))}
            />
          </FilterAccordion>

          <hr className='filter-separator' />
        </Col>
        <Col xl={12} xs={2}>
          <FilterAccordion
            label={<Trans i18nKey='app.purchase.search_page.filter.segment' />}
          >
            <CheckboxGroupField
              name='segments'
              options={segmentOptions}
              all={allLabel(sumBy(segmentOptions, 'count'))}
            />
          </FilterAccordion>
          <hr className='filter-separator' />
        </Col>
        <Col xl={12} xs={2}>
          <FilterAccordion label={<Trans i18nKey='app.common.product.brand' />}>
            <CheckboxGroupField
              name='brands'
              options={brandOptions}
              all={allLabel(sumBy(brandOptions, 'count'))}
              isSearchable
            />
          </FilterAccordion>
          <hr className='filter-separator' />
        </Col>
        <Col xl={12} xs={2}>
          <FilterAccordion
            label={<Trans i18nKey='app.common.product.season' />}
          >
            <CheckboxGroupField
              name='seasons'
              options={seasonOptions}
              all={allLabel(sumBy(seasonOptions, 'count'))}
            />
          </FilterAccordion>
          <hr className='filter-separator' />
        </Col>

        <Col xl={12} xs={2}>
          <FilterAccordion
            label={
              <Trans i18nKey='app.purchase.search_page.filter.specificity' />
            }
          >
            <CheckboxGroupField
              name='specificities'
              options={specificityOptions}
            />
          </FilterAccordion>
          <hr className='filter-separator' />
        </Col>
        <Col xl={12} xs={2}>
          <FilterAccordion
            label={<Trans i18nKey='app.common.product.homologation' />}
          >
            <CheckboxGroupField
              name='homologations'
              options={homologationOptions}
              all={allLabel(sumBy(homologationOptions, 'count'))}
            />
          </FilterAccordion>
          <hr className='filter-separator' />
        </Col>
        <Col xl={12} xs={2}>
          <FilterAccordion
            label={<Trans i18nKey='app.common.status.availibity' />}
          >
            <CheckboxGroupField
              name='availability'
              options={availabilityOptions}
            />
          </FilterAccordion>
          <hr className='filter-separator' />
        </Col>
        {inStockCount > 0 && (
          <Col xl={12} xs={2}>
            <FilterAccordion
              id='filter-local-stock'
              label={
                <Trans i18nKey='app.purchase.search_page.filter.local_stock' />
              }
            >
              <CheckboxField
                name='inStock'
                label={
                  <span data-cy='filter-local-stock-in-stock-label'>
                    {countedLabel(
                      'app.purchase.quote_search_page.product.availability.in_stock',
                      inStockCount,
                    )}
                  </span>
                }
                asTextField={false}
              />
              <CheckboxField
                name='proximityStock'
                label={
                  <span data-cy='filter-local-stock-proximity-stock-label'>
                    {countedLabel(
                      'app.purchase.quote_search_page.product.availability.neighbouring_stock',
                      products.filter(
                        (product) => product.proximityStocks.length !== 0,
                      ).length,
                    )}
                  </span>
                }
                asTextField={false}
              />
            </FilterAccordion>
            <hr className='filter-separator' />
          </Col>
        )}
        {enabledFilters.length > 0 && (
          <Col xl={12} xs={2}>
            <FilterAccordion
              label={<Trans i18nKey='app.purchase.search_page.filter.other' />}
            >
              {enabledFilters.map((filter) => (
                <CheckboxField
                  key={filter.name}
                  name={filter.name}
                  label={<Trans i18nKey={filter.label} />}
                />
              ))}
            </FilterAccordion>
            <hr className='filter-separator' />
          </Col>
        )}
        {destockingCount > 0 && (
          <Col xl={12} xs={2}>
            <FilterAccordion
              label={
                <Trans i18nKey='app.purchase.search_page.filter.destocking' />
              }
            >
              <CheckboxGroupField
                name='destocking'
                options={destockingOptions}
                all={{
                  enabled: true,
                  label: `${t('app.common.all_female')} (${sumBy(
                    destockingOptions,
                    'count',
                  )})`,
                }}
              />
            </FilterAccordion>
          </Col>
        )}
      </Row>
    </>
  );
}

export default React.memo(PurchaseFilterBar);
